import { useState } from 'react';
import { Sparkles, ChevronDown, ChevronUp, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';

interface BlogTextResult {
  title: string;
  excerpt: string;
  content: string;
  tags: string[];
  meta_title: string;
  meta_description: string;
  image_prompt: string;
  modelUsed?: string;
}

export interface AiGeneratedData {
  title: string;
  excerpt: string;
  content: string;
  tags: string;
  meta_title: string;
  meta_description: string;
  featured_image_url: string;
  image_prompt: string;
  image_error: string;
  ai_text_model?: string;
  ai_image_model?: string;
}

interface AiBlogGeneratorProps {
  onGenerated: (data: AiGeneratedData) => void;
}

type Step = 'idle' | 'generating_text' | 'generating_image' | 'done' | 'error';

export default function AiBlogGenerator({ onGenerated }: AiBlogGeneratorProps) {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState('');
  const [step, setStep] = useState<Step>('idle');
  const [error, setError] = useState('');

  async function handleGenerate() {
    if (!topic.trim()) return;

    setError('');
    setStep('generating_text');

    try {
      // Step 1: Generate text
      const textResult = await api.post<BlogTextResult>('/admin/ai/generate/text', {
        topic: topic.trim(),
      });

      // Step 2: Generate image
      setStep('generating_image');
      let imageUrl = '';
      let imageError = '';
      let aiImageModel = '';
      try {
        const imageResult = await api.post<{ url: string; modelUsed?: string }>('/admin/ai/generate/image', {
          prompt: textResult.image_prompt,
        });
        imageUrl = imageResult.url;
        aiImageModel = imageResult.modelUsed || '';
      } catch (imgErr: any) {
        imageError = imgErr.message || 'Image generation failed';
        console.warn('Image generation failed:', imageError);
      }

      setStep('done');

      onGenerated({
        title: textResult.title,
        excerpt: textResult.excerpt,
        content: textResult.content,
        tags: textResult.tags.join(', '),
        meta_title: textResult.meta_title,
        meta_description: textResult.meta_description,
        featured_image_url: imageUrl,
        image_prompt: textResult.image_prompt,
        image_error: imageError,
        ai_text_model: textResult.modelUsed || '',
        ai_image_model: aiImageModel,
      });

      // Reset after short delay
      setTimeout(() => setStep('idle'), 2000);
    } catch (err: any) {
      setStep('error');
      setError(err.message || 'Generation failed');
    }
  }

  const isGenerating = step === 'generating_text' || step === 'generating_image';

  return (
    <Card padding="none" className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-semibold text-gray-900">Generate with AI</span>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100">
          <div className="pt-4">
            <Input
              label="Topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g., Top tips for getting started"
              disabled={isGenerating}
            />
          </div>

          <p className="text-xs text-stone-500">
            Providers are automatically selected based on priority with failover.
            Configure in Settings &gt; AI Providers.
          </p>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {isGenerating && (
            <div className="flex items-center gap-2.5 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <Loader2 className="w-4 h-4 text-purple-500 animate-spin shrink-0" />
              <p className="text-sm text-purple-700">
                {step === 'generating_text' && 'Generating content...'}
                {step === 'generating_image' && 'Creating featured image...'}
              </p>
            </div>
          )}

          {step === 'done' && (
            <div className="flex items-center gap-2.5 p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
              <p className="text-sm text-green-700">Blog post generated! Review and edit below.</p>
            </div>
          )}

          <Button
            type="button"
            variant="primary"
            className="w-full"
            onClick={handleGenerate}
            disabled={!topic.trim() || isGenerating}
            isLoading={isGenerating}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Generate Blog Post
          </Button>
        </div>
      )}
    </Card>
  );
}
